import { ProgressBar } from "@/design-system";
import { useEffect, useState, type RefObject } from "react";

interface ReadingProgressProps {
  /** Contêiner do artigo. O progresso é do texto, não da página inteira. */
  targetRef: RefObject<HTMLElement | null>;
}

/**
 * Barra de progresso de leitura, fixa no topo.
 *
 * Mede o **artigo**, não o documento: contar rodapé, "Continue lendo" e CTA
 * faria a barra chegar a 60% no fim do texto e o leitor concluir que ainda
 * falta metade.
 *
 * É a `ProgressBar` do Design System — mesma régua, mesmo `role="progressbar"`
 * e mesmo respeito a `prefers-reduced-motion` de qualquer outra barra do
 * produto. Ela é decorativa para quem enxerga (a rolagem já diz o mesmo), mas
 * o rótulo existe para quem escuta a página.
 */
export const ReadingProgress = ({ targetRef }: ReadingProgressProps) => {
  const [porcento, setPorcento] = useState(0);

  useEffect(() => {
    const alvo = targetRef.current;
    if (!alvo) return;

    let frame = 0;
    const medir = () => {
      frame = 0;
      const caixa = alvo.getBoundingClientRect();
      // Quanto do artigo já passou pelo topo da janela, limitado à altura que
      // de fato rola (altura do artigo menos uma tela).
      const rolavel = caixa.height - window.innerHeight;
      if (rolavel <= 0) {
        setPorcento(caixa.bottom <= window.innerHeight ? 100 : 0);
        return;
      }
      const lido = Math.min(Math.max(-caixa.top, 0), rolavel);
      setPorcento(Math.round((lido / rolavel) * 100));
    };

    // `requestAnimationFrame` no lugar de medir a cada evento: `scroll` dispara
    // dezenas de vezes por segundo e `getBoundingClientRect` força layout.
    const agendar = () => {
      if (frame === 0) frame = window.requestAnimationFrame(medir);
    };

    medir();
    window.addEventListener("scroll", agendar, { passive: true });
    window.addEventListener("resize", agendar);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", agendar);
      window.removeEventListener("resize", agendar);
    };
  }, [targetRef]);

  return (
    // z-[60] para ficar acima do cabeçalho fixo da página pública (z-50) e
    // abaixo de modal e toast, que continuam sendo prioridade.
    <div className="fixed inset-x-0 top-0 z-[60]">
      <ProgressBar
        value={porcento}
        label="Progresso de leitura do artigo"
        hideLabel
        size="sm"
        valueText={`${porcento}% do artigo lido`}
      />
    </div>
  );
};
